/**
 * StaffAdmin
 *
 * Owner/manager view of staff for a single business listing.
 *
 *   • Lists `businessListings/{businessId}/staff/{firebaseAuthUid}` (rules
 *     allow direct reads by owner).
 *   • Joins each row with the matching `staffAccounts/{staffNumber}` doc
 *     (also readable by owner via list permission, filtered by businessId).
 *   • Owner actions, all via Cloud Functions:
 *       createStaffAccount        – invite a new staff member with a
 *                                   one-time temporary password.
 *       ownerResetStaffPassword   – generate a new temp password.
 *       ownerChangeStaffEmail     – update recovery email.
 *       setStaffActive            – activate / deactivate.
 *       deleteStaffAccount        – soft-delete (revokes auth).
 *
 * Spending (StaffRedemptionPage) lives in a separate module; this admin
 * is purely for managing the access list.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  collection,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faKey,
  faEnvelope,
  faUserSlash,
  faUserCheck,
  faTrash,
  faSpinner,
  faTimes,
  faSave,
  faCopy,
  faIdBadge,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';

import { firestore, functions } from '../../FirebaseConfig';
import { useToast } from '../ui/Toast';
import ConfirmDialog from '../ui/ConfirmDialog/ConfirmDialog';
import '../../styles/StaffAdmin.css';

const ROLE_LABELS = {
  owner: 'Owner',
  manager: 'Manager',
  redemption_only: 'Redemption only',
};

const ROLE_DESCRIPTIONS = {
  owner: 'Full access including staff management.',
  manager: 'Can manage staff, items, and redemptions.',
  redemption_only: 'Can only run the redemption scanner.',
};

function generateTempPassword() {
  // 12-char password with mixed classes — meets >=8 minimum used by CF.
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789abcdefghjkmnpqrstuvwxyz';
  const symbols = '!@#$%^&*';
  let out = '';
  for (let i = 0; i < 11; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  out += symbols[Math.floor(Math.random() * symbols.length)];
  return out;
}

function StaffAdmin({ businessId }) {
  const toast = useToast();
  const [subRows, setSubRows] = useState([]); // per-business staff subcollection
  const [accounts, setAccounts] = useState({}); // staffNumber -> staffAccount doc
  const [loading, setLoading] = useState(true);

  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    role: 'redemption_only',
    recoveryEmail: '',
    initialPassword: generateTempPassword(),
  });
  const [inviteResult, setInviteResult] = useState(null); // {staffNumber, password}
  const [busyAction, setBusyAction] = useState(null); // staffNumber currently being acted on
  const [confirmAction, setConfirmAction] = useState(null);
  const [resetResult, setResetResult] = useState(null); // {staffNumber, password}
  const staffPortalUrl = useMemo(() => {
    if (typeof window === 'undefined') return '/staff/login';
    return `${window.location.origin}/staff/login`;
  }, []);

  // ----- Subscribe to per-business staff list -----
  useEffect(() => {
    if (!businessId) return undefined;
    const subColl = collection(firestore, 'businessListings', businessId, 'staff');
    const unsub = onSnapshot(
      subColl,
      (snap) => {
        setSubRows(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[StaffAdmin] sub-staff snapshot failed', err);
        toast.error('Failed to load staff list.');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [businessId, toast]);

  // ----- Load staffAccounts (flat index) for this business via callable -----
  // Replaces a direct `staffAccounts` collection query. Firestore can't verify
  // a query's where-clause, so `where businessId == X` was readable by ANY
  // signed-in user (cross-business staff-PII leak); the `list` rule is now
  // denied and ownership is checked server-side in listBusinessStaff. Reloads
  // whenever the per-business staff subcollection changes, keeping the two
  // views in sync after any owner action (invite / reset / (de)activate /
  // delete) without wiring each handler.
  const loadAccounts = useCallback(async () => {
    if (!businessId) return;
    try {
      const fn = httpsCallable(functions, 'listBusinessStaff');
      const res = await fn({ businessId });
      const map = {};
      (res?.data?.accounts || []).forEach((a) => { map[a.staffNumber] = a; });
      setAccounts(map);
    } catch (err) {
      console.error('[StaffAdmin] listBusinessStaff failed', err);
    }
  }, [businessId]);

  useEffect(() => { loadAccounts(); }, [loadAccounts, subRows]);

  const rows = useMemo(() => {
    return subRows
      .map((sub) => {
        const acc = accounts[sub.staffNumber] || {};
        return {
          uid: sub.uid,
          staffNumber: sub.staffNumber,
          role: sub.role || acc.role,
          active: acc.active !== false && sub.active !== false,
          deleted: !!acc.deleted,
          recoveryEmail: acc.recoveryEmail || null,
          recoveryEmailVerified: !!acc.recoveryEmailVerified,
          mustChangePassword: !!acc.mustChangePassword,
          lastLoginAt: acc.lastLoginAt || null,
          addedAt: sub.addedAt || acc.createdAt || null,
        };
      })
      .filter((r) => !r.deleted)
      .sort((a, b) => (a.staffNumber || '').localeCompare(b.staffNumber || ''));
  }, [subRows, accounts]);

  // ----- CF wrappers -----
  const callCF = useCallback(async (name, payload) => {
    const fn = httpsCallable(functions, name);
    const res = await fn(payload);
    return res?.data;
  }, []);

  // ----- Actions -----
  const submitInvite = async (e) => {
    e?.preventDefault?.();
    if (!inviteForm.role) { toast.error('Choose a role.'); return; }
    if (!inviteForm.initialPassword || inviteForm.initialPassword.length < 8) {
      toast.error('Temporary password must be at least 8 characters.'); return;
    }
    setBusyAction('__invite');
    try {
      const data = await callCF('createStaffAccount', {
        businessId,
        role: inviteForm.role,
        initialPassword: inviteForm.initialPassword,
        recoveryEmail: inviteForm.recoveryEmail || null,
      });
      setInviteResult({ staffNumber: data.staffNumber, password: inviteForm.initialPassword });
      setInviteForm({
        role: 'redemption_only',
        recoveryEmail: '',
        initialPassword: generateTempPassword(),
      });
      toast.success(`Invited ${data.staffNumber}. Share the temp password securely.`);
    } catch (err) {
      console.error('[StaffAdmin] createStaffAccount failed', err);
      toast.error(err.message || 'Failed to invite staff.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleResetPassword = async (row) => {
    setBusyAction(row.staffNumber);
    try {
      const data = await callCF('ownerResetStaffPassword', {
        businessId, staffNumber: row.staffNumber,
      });
      setResetResult({ staffNumber: row.staffNumber, password: data.temporaryPassword });
      toast.success(`Password reset for ${row.staffNumber}.`);
    } catch (err) {
      console.error('[StaffAdmin] ownerResetStaffPassword failed', err);
      toast.error(err.message || 'Failed to reset password.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleToggleActive = async (row) => {
    setBusyAction(row.staffNumber);
    try {
      await callCF('setStaffActive', {
        businessId, staffNumber: row.staffNumber, active: !row.active,
      });
      toast.success(`${row.staffNumber} ${row.active ? 'deactivated' : 'reactivated'}.`);
    } catch (err) {
      console.error('[StaffAdmin] setStaffActive failed', err);
      toast.error(err.message || 'Failed to update staff status.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmAction || confirmAction.kind !== 'delete') return;
    const row = confirmAction.row;
    setConfirmAction(null);
    setBusyAction(row.staffNumber);
    try {
      await callCF('deleteStaffAccount', {
        businessId, staffNumber: row.staffNumber,
      });
      toast.info(`${row.staffNumber} removed.`);
    } catch (err) {
      console.error('[StaffAdmin] deleteStaffAccount failed', err);
      toast.error(err.message || 'Failed to remove staff.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleChangeEmail = async (row) => {
    const newEmail = window.prompt(
      `Update recovery email for ${row.staffNumber}\n(leave blank to clear)`,
      row.recoveryEmail || '',
    );
    if (newEmail === null) return; // cancelled
    setBusyAction(row.staffNumber);
    try {
      await callCF('ownerChangeStaffEmail', {
        businessId, staffNumber: row.staffNumber,
        newRecoveryEmail: newEmail || null,
      });
      toast.success('Recovery email updated. Verification sent if new email provided.');
    } catch (err) {
      console.error('[StaffAdmin] ownerChangeStaffEmail failed', err);
      toast.error(err.message || 'Failed to change recovery email.');
    } finally {
      setBusyAction(null);
    }
  };

  const copyToClipboard = (text) => {
    if (!navigator.clipboard) {
      toast.warning('Clipboard not available — please copy manually.');
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard.'),
      () => toast.error('Copy failed.'),
    );
  };

  if (!businessId) {
    return (
      <div className="staff-admin-empty">
        <FontAwesomeIcon icon={faExclamationTriangle} /> Select a business listing first.
      </div>
    );
  }

  return (
    <div className="staff-admin">
      <div className="staff-admin__header">
        <div>
          <h2><FontAwesomeIcon icon={faIdBadge} /> Staff & Access</h2>
          <p className="staff-admin__sub">
            Add crew members for this business. Each staff member gets a unique
            staff number to log in at the redemption scanner. Owners use Wix
            login — staff use staff-number + password only.
          </p>
        </div>
        <button
          className="staff-admin__new-btn"
          onClick={() => { setInviting(true); setInviteResult(null); }}
        >
          <FontAwesomeIcon icon={faPlus} /> Invite staff
        </button>
      </div>

      {loading ? (
        <div className="staff-admin__loading">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading staff…
        </div>
      ) : rows.length === 0 ? (
        <div className="staff-admin__empty">
          <p>No staff members yet.</p>
          <p className="staff-admin__empty-hint">
            Invite your first crew member to start accepting token redemptions.
          </p>
        </div>
      ) : (
        <div className="staff-admin__table">
          <div className="staff-admin__row staff-admin__row--head">
            <div>Staff #</div>
            <div>Role</div>
            <div>Recovery email</div>
            <div>Status</div>
            <div>Last login</div>
            <div className="staff-admin__actions-col">Actions</div>
          </div>
          {rows.map((row) => (
            <div
              key={row.uid || row.staffNumber}
              className={`staff-admin__row ${row.active ? '' : 'staff-admin__row--inactive'}`}
            >
              <div className="staff-admin__num">{row.staffNumber}</div>
              <div>
                <div>{ROLE_LABELS[row.role] || row.role}</div>
                {row.mustChangePassword && (
                  <div className="staff-admin__pill staff-admin__pill--warn">
                    Password change pending
                  </div>
                )}
              </div>
              <div className="staff-admin__email">
                {row.recoveryEmail || <span className="staff-admin__muted">— none —</span>}
                {row.recoveryEmail && !row.recoveryEmailVerified && (
                  <div className="staff-admin__pill staff-admin__pill--warn">Unverified</div>
                )}
              </div>
              <div>
                {row.active
                  ? <span className="staff-admin__pill staff-admin__pill--ok">Active</span>
                  : <span className="staff-admin__pill">Inactive</span>}
              </div>
              <div className="staff-admin__muted">
                {row.lastLoginAt
                  ? new Date(row.lastLoginAt).toLocaleString()
                  : '—'}
              </div>
              <div className="staff-admin__actions-col">
                <button
                  className="staff-admin__icon-btn"
                  title="Reset password"
                  disabled={busyAction === row.staffNumber}
                  onClick={() => handleResetPassword(row)}
                >
                  <FontAwesomeIcon icon={faKey} />
                </button>
                <button
                  className="staff-admin__icon-btn"
                  title="Change recovery email"
                  disabled={busyAction === row.staffNumber}
                  onClick={() => handleChangeEmail(row)}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
                <button
                  className="staff-admin__icon-btn"
                  title={row.active ? 'Deactivate' : 'Reactivate'}
                  disabled={busyAction === row.staffNumber}
                  onClick={() => handleToggleActive(row)}
                >
                  <FontAwesomeIcon icon={row.active ? faUserSlash : faUserCheck} />
                </button>
                <button
                  className="staff-admin__icon-btn staff-admin__icon-btn--danger"
                  title="Remove staff"
                  disabled={busyAction === row.staffNumber}
                  onClick={() => setConfirmAction({ kind: 'delete', row })}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Invite modal ---- */}
      {inviting && (
        <div className="staff-admin__modal-backdrop" onClick={() => setInviting(false)}>
          <div className="staff-admin__modal" onClick={(e) => e.stopPropagation()}>
            <h3>Invite staff member</h3>

            {inviteResult ? (
              <>
                <div className="staff-admin__success">
                  <p>
                    Staff number <strong>{inviteResult.staffNumber}</strong> created.
                  </p>
                  <p>Share this temporary password with them securely. They'll be
                    required to change it at first login.</p>
                  <p style={{ marginTop: '0.7rem' }}>
                    Staff portal link: <a href={staffPortalUrl} target="_blank" rel="noreferrer">{staffPortalUrl}</a>
                  </p>
                  <ol style={{ margin: '0.6rem 0 0', paddingLeft: '1.2rem' }}>
                    <li>Open the Staff Portal link above.</li>
                    <li>Sign in with staff number <strong>{inviteResult.staffNumber}</strong> and the temporary password.</li>
                    <li>Check recovery email for the verification code and enter it during setup step 2.</li>
                  </ol>
                  <div className="staff-admin__credentials">
                    <code>{inviteResult.password}</code>
                    <button
                      className="staff-admin__btn staff-admin__btn--ghost"
                      onClick={() => copyToClipboard(inviteResult.password)}
                    >
                      <FontAwesomeIcon icon={faCopy} /> Copy
                    </button>
                  </div>
                  <div className="staff-admin__credentials" style={{ marginTop: '0.65rem' }}>
                    <code>{staffPortalUrl}</code>
                    <button
                      className="staff-admin__btn staff-admin__btn--ghost"
                      onClick={() => copyToClipboard(staffPortalUrl)}
                    >
                      <FontAwesomeIcon icon={faCopy} /> Copy link
                    </button>
                  </div>
                  <div className="staff-admin__credentials" style={{ marginTop: '0.65rem' }}>
                    <button
                      className="staff-admin__btn staff-admin__btn--ghost"
                      onClick={() => copyToClipboard(
                        [
                          'Pirate\'s Guild staff setup steps',
                          `Staff portal: ${staffPortalUrl}`,
                          `Staff number: ${inviteResult.staffNumber}`,
                          `Temporary password: ${inviteResult.password}`,
                          'After signing in, open setup step 2 and enter the verification code from your recovery email.',
                        ].join('\n')
                      )}
                    >
                      <FontAwesomeIcon icon={faCopy} /> Copy full setup instructions
                    </button>
                  </div>
                </div>
                <div className="staff-admin__modal-actions">
                  <button
                    className="staff-admin__btn staff-admin__btn--ghost"
                    onClick={() => { setInviting(false); setInviteResult(null); }}
                  >
                    Done
                  </button>
                  <button
                    className="staff-admin__btn staff-admin__btn--primary"
                    onClick={() => setInviteResult(null)}
                  >
                    Invite another
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={submitInvite} className="staff-admin__form">
                <label className="staff-admin__field">
                  <span>Role</span>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
                  >
                    <option value="redemption_only">Redemption only — scan & confirm only</option>
                    <option value="manager">Manager — can manage staff & items</option>
                    <option value="owner">Owner — full access</option>
                  </select>
                  <small className="staff-admin__field-hint">
                    {ROLE_DESCRIPTIONS[inviteForm.role]}
                  </small>
                </label>

                <label className="staff-admin__field">
                  <span>Recovery email <em>(optional, for password resets)</em></span>
                  <input
                    type="email"
                    value={inviteForm.recoveryEmail}
                    onChange={(e) => setInviteForm((f) => ({ ...f, recoveryEmail: e.target.value }))}
                    placeholder="staff@example.com"
                  />
                </label>

                <label className="staff-admin__field">
                  <span>Temporary password</span>
                  <div className="staff-admin__pw-row">
                    <input
                      type="text"
                      value={inviteForm.initialPassword}
                      onChange={(e) => setInviteForm((f) => ({ ...f, initialPassword: e.target.value }))}
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="staff-admin__btn staff-admin__btn--ghost"
                      onClick={() => setInviteForm((f) => ({ ...f, initialPassword: generateTempPassword() }))}
                    >
                      Regenerate
                    </button>
                  </div>
                  <small className="staff-admin__field-hint">
                    They'll be required to change this at first login.
                  </small>
                </label>

                <div className="staff-admin__modal-actions">
                  <button
                    type="button"
                    className="staff-admin__btn staff-admin__btn--ghost"
                    onClick={() => setInviting(false)}
                    disabled={busyAction === '__invite'}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                  <button
                    type="submit"
                    className="staff-admin__btn staff-admin__btn--primary"
                    disabled={busyAction === '__invite'}
                  >
                    {busyAction === '__invite'
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Creating…</>
                      : <><FontAwesomeIcon icon={faSave} /> Create staff</>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ---- Reset-password result modal ---- */}
      {resetResult && (
        <div className="staff-admin__modal-backdrop" onClick={() => setResetResult(null)}>
          <div className="staff-admin__modal" onClick={(e) => e.stopPropagation()}>
            <h3>New temporary password</h3>
            <p>
              Share this with <strong>{resetResult.staffNumber}</strong> securely.
              They'll need to change it at next login.
            </p>
            <div className="staff-admin__credentials">
              <code>{resetResult.password}</code>
              <button
                className="staff-admin__btn staff-admin__btn--ghost"
                onClick={() => copyToClipboard(resetResult.password)}
              >
                <FontAwesomeIcon icon={faCopy} /> Copy
              </button>
            </div>
            <div className="staff-admin__modal-actions">
              <button
                className="staff-admin__btn staff-admin__btn--primary"
                onClick={() => setResetResult(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmAction?.kind === 'delete'}
        title="Remove staff member?"
        message={confirmAction?.row
          ? `${confirmAction.row.staffNumber} will lose access immediately. Their redemption history is preserved.`
          : ''}
        confirmText="Remove"
        cancelText="Keep"
        type="danger"
        onConfirm={handleDelete}
        onClose={() => setConfirmAction(null)}
      />
    </div>
  );
}

export default StaffAdmin;
